import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Box,
    useMediaQuery,
    CircularProgress,
    Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { Reference } from '../../api/interfaces/Reference';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import DOMPurify from 'dompurify';
import { markAsFavorite } from '../../api/services/references';
import { handleApiError } from '../../utils/errorHandler';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';

interface ReferenceDialogProps {
    open: boolean;
    onClose: () => void;
    reference: Reference | null;
    isLoading?: boolean;
    onFavoriteToggle?: (referenceId: string, isFavorite: boolean) => void;
    referenceThumbnail?: string;
}

const ReadReferenceDialog: React.FC<ReferenceDialogProps> = ({ open, onClose, reference, isLoading, onFavoriteToggle, referenceThumbnail }) => {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [isFavorite, setIsFavorite] = useState<boolean>(false);
    const [isMarkLoading, setIsMarkLoading] = useState<boolean>(false);

    useEffect(() => {
        if (reference) {
            setIsFavorite(reference.isFavorite)
        }
    }, [reference]);

    const handleMarkAsFavorite = async (referenceId: string) => {
        if (isMarkLoading) return;

        setIsMarkLoading(true);
        const action = isFavorite ? "remove" : "add";

        try {
            const response = await markAsFavorite(referenceId, action);
            if (response.result === "success") {
                setIsFavorite(!isFavorite);
                onFavoriteToggle?.(referenceId, !isFavorite);
            }
        } catch (err) {
            handleApiError(err, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsMarkLoading(false);
        }
    }

    const handleDialogClose = () => {
        setIsFavorite(false);
        onClose();
    }

    return (
        <Dialog
            open={open}
            maxWidth={isLoading ? "xs" : "md"}
            fullWidth
            scroll="paper"
            disableScrollLock
            onClose={() => {
                handleDialogClose()
            }}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: 'transparent',
                        borderColor: 'none',
                        boxShadow: 'none',
                        borderRadius: '16px'
                    },
                },
            }}
        >
            <DialogContent
                sx={{
                    display: 'flex',
                    p: 0,
                    backgroundColor: 'background.paper',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    minHeight: !isMobile || isLoading ? '300px' : '1000px',
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'background.paper'
                        }}
                    >
                        <Box
                            component="img"
                            src={referenceThumbnail ? referenceThumbnail : reference?.title}
                            alt={referenceThumbnail ? 'referenceThumbnail' : reference?.title}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    </Box>
                ) : reference ? (
                    <Fade in={!isLoading} timeout={500} unmountOnExit>
                        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                            {/* Center Content */}
                            <Box
                                sx={{
                                    flex: '1 1 50%',
                                    p: 3,
                                    position: 'relative',
                                    overflowY: 'auto',
                                    maxHeight: '87vh',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <IconButton
                                    onClick={handleDialogClose}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>

                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        left: 12,
                                        zIndex: 3,
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        borderRadius: '4px',
                                        py: '8px',
                                        px: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                        },
                                    }}
                                    onClick={() => handleMarkAsFavorite(reference._id)}
                                >
                                    {isMarkLoading ? (
                                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                                    )
                                        :
                                        <>
                                            {!isFavorite ? (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: 'white',
                                                            lineHeight: 1,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Add to favorites
                                                    </Typography>
                                                    <StarIcon sx={{ color: theme.palette.secondary.main, fontSize: 16 }} />
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: 'white',
                                                            lineHeight: 1,
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        Remove to favorites
                                                    </Typography>
                                                    <StarBorderOutlinedIcon sx={{ color: theme.palette.secondary.main, fontSize: 16 }} />
                                                </>
                                            )}
                                        </>
                                    }
                                </IconButton>


                                <Typography
                                    variant="h5"
                                    gutterBottom
                                    color={theme.palette.common.black}
                                    sx={{
                                        mt: 5,
                                        mb: 3,
                                        fontWeight: 600,
                                    }}
                                >
                                    {reference.title}
                                </Typography>
                                <Box
                                    sx={{
                                        '& img': {
                                            maxWidth: '100%',
                                            height: 'auto',
                                            display: 'block',
                                            margin: '1rem 0',
                                            justifyContent: 'center',
                                            justifySelf: 'center'
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reference.content) }}
                                />
                            </Box>
                        </Box>
                    </Fade>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default ReadReferenceDialog;
